import { Controller } from '@hotwired/stimulus';
import { toolbarPopoverPosition } from '../lib/toolbar_popover_position.js';

export default class extends Controller {
  static values = { open: Boolean, anchorId: String, presentation: { type: String, default: 'anchored' } };

  connect() {
    this.connected = true;
    this.schedulePosition = () => {
      if (!this.connected || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.refresh();
      });
    };
    this.handleKeydown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || !this.openValue) return;
      event.preventDefault();
      event.stopPropagation();
      this.openValue = false;
      this.hide();
      this.dispatch('close');
    };
    this.stopWheel = (event) => event.stopPropagation();
    this.handleReposition = () => this.reposition();
    this.element.addEventListener('keydown', this.handleKeydown);
    this.element.addEventListener('toolbar-popover:reposition', this.handleReposition);
    this.element.addEventListener('wheel', this.stopWheel, { passive: true });
    window.addEventListener('resize', this.schedulePosition);
    document.addEventListener('scroll', this.schedulePosition, true);
    window.visualViewport?.addEventListener('resize', this.schedulePosition);
    window.visualViewport?.addEventListener('scroll', this.schedulePosition);
    this.resizeObserver = new ResizeObserver(this.schedulePosition);
    this.resizeObserver.observe(this.element);
    this.mutationObserver = new MutationObserver(this.schedulePosition);
    this.refresh();
  }

  disconnect() {
    this.connected = false;
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.element.removeEventListener('keydown', this.handleKeydown);
    this.element.removeEventListener('toolbar-popover:reposition', this.handleReposition);
    this.element.removeEventListener('wheel', this.stopWheel);
    window.removeEventListener('resize', this.schedulePosition);
    document.removeEventListener('scroll', this.schedulePosition, true);
    window.visualViewport?.removeEventListener('resize', this.schedulePosition);
    window.visualViewport?.removeEventListener('scroll', this.schedulePosition);
    this.hide();
    this.anchor = null;
  }

  reposition() {
    if (this.connected) this.refresh();
  }

  openValueChanged() {
    if (this.connected) this.refresh();
  }

  anchorIdValueChanged() {
    if (this.connected) this.refresh();
  }

  presentationValueChanged() {
    if (this.connected) this.refresh();
  }

  refresh() {
    if (!this.openValue) {
      this.mutationObserver.disconnect();
      this.hide();
      return;
    }

    // A Turbo morph can replace an anchor without replacing this controller.
    this.mutationObserver.observe(document.body, { childList: true, subtree: true });
    if (this.presentationValue === 'panel') {
      this.positionPanel();
      return;
    }
    const anchor = document.getElementById(this.anchorIdValue);
    if (anchor !== this.anchor) {
      if (this.anchor) this.resizeObserver.unobserve(this.anchor);
      this.anchor = anchor;
      if (anchor) this.resizeObserver.observe(anchor);
    }
    if (!anchor?.getClientRects().length || !this.element.isConnected) {
      this.hide();
      return;
    }

    const visual = window.visualViewport;
    this.element.style.height = '';
    const viewport = {
      left: visual?.offsetLeft || 0,
      top: visual?.offsetTop || 0,
      width: visual?.width || document.documentElement.clientWidth,
      height: visual?.height || window.innerHeight
    };
    // Apply viewport limits before measuring to account for wrapped content.
    this.element.style.maxWidth = `${Math.max(0, viewport.width - 16)}px`;
    this.element.style.maxHeight = `${Math.max(0, viewport.height - 16)}px`;
    this.element.hidden = false;
    if (typeof this.element.showPopover === 'function' && !this.element.matches(':popover-open')) {
      this.element.showPopover();
    }
    const position = toolbarPopoverPosition(
      anchor.getBoundingClientRect(),
      this.element.getBoundingClientRect(),
      viewport
    );
    this.element.style.left = `${position.left}px`;
    this.element.style.top = `${position.top}px`;
  }

  positionPanel() {
    // The editor owns this panel. Its layer row may be hidden, replaced or
    // scrolled away; neither selection nor the live Stimulus scope moves.
    const editor = this.element.closest('.game-map-editor');
    const controls = editor?.querySelector('[data-konva-map-target~="mobileControls"]');
    if (!editor || !controls || !this.element.isConnected) return this.hide();
    const bounds = editor.getBoundingClientRect();
    const landscape = bounds.width > bounds.height && bounds.width >= 540;
    const dock = controls.getBoundingClientRect();
    const zoom = editor.querySelector('[data-konva-map-target~="mobileZoomControls"]')?.getBoundingClientRect();
    const visual = window.visualViewport;
    const top = Math.max(bounds.top, zoom?.bottom || 0, visual?.offsetTop || 0) + 8;
    const bottom = Math.min(dock.top - 8, (visual?.offsetTop || 0) + (visual?.height || window.innerHeight) - 8);
    const available = Math.max(0, bottom - top);
    this.element.style.maxWidth = `${Math.max(0, Math.min(bounds.width - 16, landscape ? 288 : 480))}px`;
    this.element.style.height = '';
    this.element.style.maxHeight = `${available}px`;
    this.element.hidden = false;
    if (typeof this.element.showPopover === 'function' && !this.element.matches(':popover-open'))
      this.element.showPopover();
    const size = this.element.getBoundingClientRect();
    this.element.style.left = `${landscape ? bounds.left + 8 : bounds.left + (bounds.width - size.width) / 2}px`;
    this.element.style.top = `${Math.max(top, bottom - size.height)}px`;
  }

  hide() {
    if (typeof this.element.hidePopover === 'function' && this.element.matches(':popover-open')) {
      this.element.hidePopover();
    }
    this.element.hidden = true;
  }
}
